import {
  EntryComponentConfig,
  Envelope,
  ContentGUID,
  TextContent,
  AudioContent,
  DocumentContent,
  VideoContent,
  ScormContent,
  Enrollment,
  PreloadSettings,
  PreloadResource,
} from '../shared/types/common.model';
import { createQueryString } from '../shared/util/create-query-string';
import { DataService } from '../services/data_service';
import { ContentDeliveryClient } from './content-delivery-client';
import { Module } from '../shared/types/common.model';

export class ContentDeliveryMicroservice implements ContentDeliveryClient {
  private dataService: DataService;

  private readonly API_VERSION = '/api/v2';

  constructor(internalConfig: EntryComponentConfig) {
    this.dataService = DataService.getInstance(internalConfig.baseUrl);
  }

  /** Fetch a single text content */
  async getText(
    options: ContentGUID & {
      /** Signal object used to abort requests */
      signal?: AbortSignal;
    }
  ) {
    const { signal, ...parameters } = options;

    const response = await this.dataService.get<Envelope<Array<TextContent>>>(
      `${this.API_VERSION}/content/texts?${createQueryString(parameters)}`,
      signal
    );

    return response.data[0];
  }

  /** Fetch a single audio content */
  async getAudio(
    options: ContentGUID & {
      /** Signal object used to abort requests */
      signal?: AbortSignal;
    }
  ) {
    const { signal, ...parameters } = options;

    const response = await this.dataService.get<Envelope<Array<AudioContent>>>(
      `${this.API_VERSION}/content/audios?${createQueryString(parameters)}`,
      signal
    );

    return response.data[0];
  }

  /** Fetch a single document content */
  async getDocument(
    options: ContentGUID & {
      /** Signal object used to abort requests */
      signal?: AbortSignal;
    }
  ) {
    const { signal, ...parameters } = options;

    const response = await this.dataService.get<Envelope<Array<DocumentContent>>>(
      `${this.API_VERSION}/content/documents?${createQueryString(parameters)}`,
      signal
    );

    return response.data[0];
  }

  /** Fetch a single video content */
  async getVideo(
    options: ContentGUID & {
      /** Signal object used to abort requests */
      signal?: AbortSignal;
    }
  ) {
    const { signal, ...parameters } = options;

    const response = await this.dataService.get<Envelope<Array<VideoContent>>>(
      `${this.API_VERSION}/content/videos?${createQueryString(parameters)}`,
      signal
    );

    return response.data[0];
  }

  /** Fetch a single scorm content */
  async getScorm(
    options: ContentGUID & {
      /** Signal object used to abort requests */
      signal?: AbortSignal;
    }
  ) {
    const { signal, ...parameters } = options;

    const response = await this.dataService.get<Envelope<Array<ScormContent>>>(
      `${this.API_VERSION}/content/scorms?${createQueryString(parameters)}`,
      signal
    );

    return response.data[0];
  }

  /** Fetch the course structure */
  async getCourseModules() {
    // TODO: Implement
    const response = await this.dataService.get<Envelope<Module[]>>(`${this.API_VERSION}/content/course-structure`);

    return response.data;
  }

  async getEnrollment(): Promise<Enrollment> {
    throw new Error('Method not implemented.');
  }

  async getEnrollmentComponent(): Promise<any> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }

  async getFlags(): Promise<Record<string, unknown>> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }
  async getOnDescriptionPage(): Promise<boolean> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }

  async getIsPreview(): Promise<boolean> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }

  async getSettings(): Promise<PreloadSettings> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }

  async getWasJustCompleted(): Promise<boolean> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }

  async getSocialSharing(): Promise<PreloadResource['social_sharing']['attributes']> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }

  async getAwardedBadges(): Promise<PreloadResource['awarded_badges']['data']> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }

  async getNextCourseOnPath(): Promise<PreloadResource['next_course_on_path_id']> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }

  async getEmbeddingSettings(): Promise<{
    hide_module_sidebar: PreloadResource['hide_module_sidebar'];
    embedded_content: PreloadResource['embedded_content'];
    embedded_content_autoplay: PreloadResource['embedded_content_autoplay'];
  }> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }

  async getScormSettings(): Promise<{
    tincan_completion_tracking: PreloadResource['tincan_completion_tracking'];
  }> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }

  async getPortalFeatureFlags(): Promise<{
    content_details_redesign_enabled: PreloadResource['content_details_redesign_enabled'];
    concurrent_and_series_sessions: PreloadResource['concurrent_and_series_sessions'];
  }> {
    // TODO: Implement
    throw new Error('Method not implemented.');
  }
}
